import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import FoundingMemberCallout from "@/components/FoundingMemberCallout";

/** Pricing page (www.frazzlecrm.com/pricing). Live Stripe checkout via signup?plan=. */

type Tier = {
  name: string;
  price: string;
  period?: string;
  blurb: string;
  cta: string;
  ctaHref: string;
  highlight?: boolean;
  features: string[];
};

const TIERS: Tier[] = [
  {
    name: "Free",
    price: "$0",
    blurb: "The full toolkit, sized for your first funnel. No credit card.",
    cta: "Start free",
    ctaHref: `${APP_URL}/signup`,
    features: [
      "20 flows (automations), 10 nodes each",
      "30 free-floating map nodes",
      "1 funnel map · 1 workspace · 1 user",
      "500 contacts",
      "Email & SMS steps (your credentials)",
      "Templates, appointments, import/export",
      "Broken-link watchdog",
    ],
  },
  {
    name: "Starter",
    price: "$29",
    period: "/mo",
    blurb: "Everything in Free, with room for a funnel that's running for real.",
    cta: "Start with Starter",
    ctaHref: `${APP_URL}/signup?plan=starter`,
    features: [
      "50 flows, 25 nodes each",
      "150 free-floating map nodes",
      "5 funnel maps",
      "3 users",
      "5,000 contacts",
    ],
  },
  {
    name: "Pro",
    price: "$79",
    period: "/mo",
    blurb: "For teams running their whole business on flows.",
    cta: "Go Pro",
    ctaHref: `${APP_URL}/signup?plan=pro`,
    highlight: true,
    features: [
      "Unlimited flows, nodes & maps",
      "3 workspaces",
      "10 users",
      "25,000 contacts",
      "AI access (MCP) for your assistants",
      "Public API keys",
      "Priority support",
    ],
  },
  {
    name: "Scale",
    price: "$199",
    period: "/mo",
    blurb: "Everything in Pro, for agencies and multi-brand operators.",
    cta: "Start with Scale",
    ctaHref: `${APP_URL}/signup?plan=scale`,
    features: [
      "Unlimited workspaces & users",
      "100,000 contacts",
      "Dedicated support",
      "White-label (coming soon)",
    ],
  },
];

export default function Pricing() {
  return (
    <SiteShell title="Pricing — FrazzleCRM">
      <section className="mx-auto max-w-6xl px-6 pb-6 pt-16 text-center">
        <h1 className="text-[38px] font-extrabold tracking-tight text-slate-900">
          Simple pricing that grows with your funnel
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-[15px] text-slate-600">
          Start free with no credit card. Upgrade when you hit a limit —
          everything you&apos;ve built comes with you. Pay annually and get
          two months free.
        </p>
      </section>

      {/* A visitor comparing prices should not have to find out elsewhere
          that a one-time option exists. */}
      <section className="mx-auto max-w-5xl px-6 pb-10">
        <FoundingMemberCallout note="The monthly plans below are unchanged — this is a one-time alternative, open until the 100 spots are gone." />
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-20">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {TIERS.map((t) => (
            <div
              key={t.name}
              className={`relative flex flex-col rounded-2xl border bg-white p-6 ${
                t.highlight
                  ? "border-indigo-400 shadow-xl shadow-indigo-100 ring-2 ring-indigo-500/20"
                  : "border-slate-200 shadow-sm"
              }`}
            >
              {t.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-indigo-600 px-3 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
                  Most popular
                </span>
              )}
              <h3 className="text-[15px] font-bold text-slate-900">{t.name}</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-[36px] font-extrabold tracking-tight text-slate-900">
                  {t.price}
                </span>
                {t.period && (
                  <span className="text-[14px] text-slate-400">{t.period}</span>
                )}
              </div>
              <p className="mt-1.5 min-h-[40px] text-[13px] leading-snug text-slate-500">
                {t.blurb}
              </p>
              <a
                href={t.ctaHref}
                className={`mt-4 rounded-lg px-4 py-2.5 text-center text-[13.5px] font-semibold ${
                  t.highlight
                    ? "bg-indigo-600 text-white hover:bg-indigo-500"
                    : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {t.cta}
              </a>
              <ul className="mt-5 space-y-2 text-[13px] text-slate-600">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="mt-0.5 text-emerald-500">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* FAQ-ish reassurance */}
        <div className="mx-auto mt-14 grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-2">
          {[
            {
              q: "What happens when I hit a free-tier limit?",
              a: "Nothing breaks. You'll see an upgrade prompt, everything you've built stays intact, and the moment you upgrade the limit lifts in place.",
            },
            {
              q: "What counts as a flow?",
              a: "One automation — a trigger plus its steps. Flows drawn on the map that aren't running yet don't count against your limit until you make them real.",
            },
            {
              q: "Do I need a credit card for the free tier?",
              a: "No. Free is free — sign up with an email and start mapping.",
            },
            {
              q: "Is there a way to pay once instead of monthly?",
              a: "Yes, while it lasts: the Founding Member offer above is one $397 payment with no recurring FrazzleCRM subscription, limited to 100 people. Email and SMS still send through your own providers either way.",
            },
            {
              q: "Can my AI use my plan?",
              a: "Pro and above include MCP access: connect Claude, ChatGPT, or any MCP client with an API key and it works your workspace under the same limits you do.",
            },
          ].map((f) => (
            <div key={f.q}>
              <h4 className="text-[14px] font-bold text-slate-900">{f.q}</h4>
              <p className="mt-1 text-[13.5px] leading-relaxed text-slate-600">
                {f.a}
              </p>
            </div>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
